import { useEffect, useState } from 'react';
import { useKlantenStore, type ContactCreateFields } from '../../stores/klanten-store';

interface Props {
  isOpen: boolean;
  accountId: string;
  accountName: string;
  onClose: () => void;
  onSuccess: (contactId: string) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  title: string;
  department: string;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobilePhone: '',
  title: '',
  department: '',
};

/**
 * Voeg een contactpersoon toe aan een bestaand Business Account. Lichtgewicht
 * dialog (geen wizard). LastName verplicht. Dup-check op (Email + AccountId)
 * gebeurt server-side; bij conflict tonen we een inline message met link naar
 * de bestaande contactpersoon.
 */
export function AddContactDialog({ isOpen, accountId, accountName, onClose, onSuccess }: Props) {
  const createContact = useKlantenStore((s) => s.createContact);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setForm(EMPTY);
      setError(null);
      setExistingId(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const trimmedLastName = form.lastName.trim();
  const canSubmit = trimmedLastName.length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    setExistingId(null);

    const fields: ContactCreateFields = {
      LastName: trimmedLastName,
    };
    const firstName = form.firstName.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const mobile = form.mobilePhone.trim();
    const title = form.title.trim();
    const department = form.department.trim();
    if (firstName) fields.FirstName = firstName;
    if (email) fields.Email = email;
    if (phone) fields.Phone = phone;
    if (mobile) fields.MobilePhone = mobile;
    if (title) fields.Title = title;
    if (department) fields.Department = department;

    const result = await createContact(accountId, fields);
    setSubmitting(false);

    if (!result.ok) {
      if (result.code === 'DUPLICATE_FOUND' && result.existingContactId) {
        setExistingId(result.existingContactId);
        setError(result.error || 'Contact met dit emailadres bestaat al onder deze klant');
      } else {
        setError(result.error || 'Aanmaken mislukt');
      }
      return;
    }
    onSuccess(result.contactId);
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-base font-semibold text-slate-900">Contactpersoon toevoegen</h2>
            <p className="text-xs text-slate-500 mt-1">Voor {accountName}</p>
          </div>

          <div className="px-6 py-4 space-y-3">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
                {existingId && (
                  <div className="mt-1 text-xs">
                    Ga naar de bestaande contactpersoon: <code className="font-mono">{existingId}</code>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Voornaam</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Achternaam <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  required
                  disabled={submitting}
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">E-mailadres</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                disabled={submitting}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Telefoon</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Mobiel</label>
                <input
                  type="tel"
                  value={form.mobilePhone}
                  onChange={(e) => setForm({ ...form, mobilePhone: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Functie</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Afdeling</label>
                <input
                  type="text"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Opslaan…' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
